/**
 * Attribute-style form for property cards: name, DIGIPIN, area, side lengths, address, QR.
 */
import { Box, Typography, Stack, Chip, Divider, Button, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import StraightenIcon from '@mui/icons-material/Straighten';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import PlaceIcon from '@mui/icons-material/Place';
import { computeParcelMetrics, type LonLat } from '../../utils/propertyCardGeometry';

export type PropertyAttributeFormProps = {
  name?: string;
  properties?: Record<string, unknown>;
  geometry?: { type: string; coordinates: unknown } | null;
  shareUrl?: string | null;
  onCopyShare?: () => void;
  compact?: boolean;
};

function ringFromGeometry(geometry: any): LonLat[] | null {
  if (!geometry || geometry.type !== 'Polygon') return null;
  const ring = geometry.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) return null;
  return ring.map((c: number[]) => [Number(c[0]), Number(c[1])] as LonLat);
}

export function buildPropertyAttributeModel(
  props: Record<string, unknown> = {},
  geometry?: { type: string; coordinates: unknown } | null,
  shareUrl?: string | null
) {
  const name = String(props.name || props.Name || props.parcel_name || 'Property');
  const digipin = String(props.DIGIPIN || props.digipin || '');
  const address = String(props.Address || props.address || '');
  const owner = String(props.Owner || props.owner || '');
  const notes = String(props.Notes || props.notes || '');
  const parcelId = String(props.Parcel_ID || props.parcel_id || '');

  // Prefer stored labels, else compute from geometry
  let areaLabel = String(props.Area_Label || props.area_label || '');
  let perimeterLabel = String(props.Perimeter_Label || props.perimeter_label || '');
  const sideLabels: string[] = [];

  // Stored Side_1_Label or Side_1_m
  for (let i = 1; i <= 32; i++) {
    const lab = props[`Side_${i}_Label`] || props[`Side_${i}_label`];
    if (lab) {
      sideLabels.push(String(lab));
      continue;
    }
    const m = props[`Side_${i}_m`] ?? props[`Side_${i}_M`];
    if (m != null && m !== '') {
      const num = Number(m);
      sideLabels.push(Number.isFinite(num) ? `S${i}: ${num.toFixed(2)} m` : `S${i}: ${m}`);
    }
  }

  if (geometry) {
    const ring = ringFromGeometry(geometry);
    if (ring) {
      const corners =
        ring.length > 1 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]
          ? ring.slice(0, -1)
          : ring;
      const metrics = computeParcelMetrics(corners);
      if (metrics) {
        if (!areaLabel) areaLabel = metrics.areaLabel;
        if (!perimeterLabel) perimeterLabel = metrics.perimeterLabel;
        if (sideLabels.length === 0) {
          metrics.sides.forEach((s) => sideLabels.push(s.label));
        }
      }
    }
  }

  if (!areaLabel && props.Area_Acres != null) {
    areaLabel = `${props.Area_Acres} ac`;
    if (props.Area_SqFt) areaLabel += ` · ${props.Area_SqFt} sq ft`;
  }

  const qrSource =
    String(props.QR_URL || props.qr_url || '') ||
    String(props.Share_URL || props.share_url || '') ||
    shareUrl ||
    '';

  const qrImg = qrSource
    ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&data=${encodeURIComponent(qrSource)}`
    : null;

  return {
    name,
    digipin,
    address,
    owner,
    notes,
    parcelId,
    areaLabel,
    perimeterLabel,
    sideLabels,
    qrSource,
    qrImg,
  };
}

export default function PropertyAttributeForm({
  name: nameProp,
  properties = {},
  geometry,
  shareUrl,
  onCopyShare,
  compact = false,
}: PropertyAttributeFormProps) {
  const model = buildPropertyAttributeModel(properties, geometry, shareUrl);
  const title = nameProp || model.name;

  const fieldSx = {
    '& .MuiInputBase-root': {
      color: '#F1F5E9',
      bgcolor: 'rgba(0,0,0,0.28)',
      fontSize: 13,
      fontWeight: 600,
    },
    '& .MuiInputLabel-root': { color: '#889f8f', fontSize: 11, fontWeight: 700 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(199,236,155,0.15)' },
    mb: 1.25,
  } as const;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <SquareFootIcon sx={{ color: '#84cc16', fontSize: 18 }} />
        <Typography
          sx={{ color: '#c7ec9b', fontWeight: 800, fontSize: 12, letterSpacing: '0.08em' }}
        >
          ATTRIBUTE TABLE
        </Typography>
      </Stack>

      <TextField
        fullWidth
        size="small"
        label="Name"
        value={title}
        InputProps={{ readOnly: true }}
        sx={fieldSx}
      />
      {model.parcelId && (
        <TextField
          fullWidth
          size="small"
          label="Parcel ID"
          value={model.parcelId}
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
      )}
      {model.owner && (
        <TextField
          fullWidth
          size="small"
          label="Owner"
          value={model.owner}
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
      )}
      {model.digipin && (
        <TextField
          fullWidth
          size="small"
          label="DIGIPIN"
          value={model.digipin}
          InputProps={{ readOnly: true }}
          sx={{
            ...fieldSx,
            '& .MuiInputBase-root': {
              ...fieldSx['& .MuiInputBase-root'],
              color: '#7dd3fc',
              fontWeight: 800,
            },
          }}
        />
      )}
      {model.areaLabel && (
        <TextField
          fullWidth
          size="small"
          label="Area"
          value={model.areaLabel}
          InputProps={{ readOnly: true }}
          sx={{
            ...fieldSx,
            '& .MuiInputBase-root': {
              ...fieldSx['& .MuiInputBase-root'],
              color: '#a3e635',
            },
          }}
        />
      )}
      {model.perimeterLabel && (
        <TextField
          fullWidth
          size="small"
          label="Perimeter"
          value={model.perimeterLabel.replace(/^Perimeter:\s*/i, '')}
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
      )}
      {model.address && (
        <TextField
          fullWidth
          size="small"
          label="Address"
          value={model.address}
          multiline
          minRows={compact ? 2 : 2}
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
      )}

      {model.sideLabels.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
            <StraightenIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
            <Typography
              sx={{ color: '#889f8f', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em' }}
            >
              SIDE LENGTHS
            </Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
            {model.sideLabels.map((lab) => (
              <Chip
                key={lab}
                size="small"
                label={lab}
                sx={{
                  bgcolor: 'rgba(132,204,22,0.12)',
                  color: '#d9f99d',
                  fontWeight: 700,
                  fontSize: 11,
                  border: '1px solid rgba(132,204,22,0.3)',
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {model.notes && (
        <TextField
          fullWidth
          size="small"
          label="Notes"
          value={model.notes}
          multiline
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />

      {/* QR block */}
      <Stack
        direction={compact ? 'column' : { xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems="center"
      >
        {model.qrImg ? (
          <Box
            sx={{
              p: 1,
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            }}
          >
            <Box
              component="img"
              src={model.qrImg}
              alt="QR code for this property"
              sx={{ width: compact ? 120 : 140, height: compact ? 120 : 140, display: 'block' }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              border: '1px dashed rgba(148,163,184,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748B',
            }}
          >
            <QrCode2Icon />
          </Box>
        )}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
            <PlaceIcon sx={{ color: '#38bdf8', fontSize: 16 }} />
            <Typography
              sx={{ color: '#7dd3fc', fontWeight: 800, fontSize: 11, letterSpacing: '0.06em' }}
            >
              QR · PUBLIC LINK
            </Typography>
          </Stack>
          <Typography sx={{ color: '#94A3B8', fontSize: 11, mb: 1, lineHeight: 1.4 }}>
            Scan or copy to open this property on GeoVera (free).
          </Typography>
          {model.qrSource && (
            <Typography
              sx={{
                color: '#64748B',
                fontSize: 10,
                fontFamily: 'ui-monospace, monospace',
                wordBreak: 'break-all',
                mb: 1,
              }}
            >
              {model.qrSource}
            </Typography>
          )}
          {onCopyShare && model.qrSource && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={onCopyShare}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderColor: 'rgba(132,204,22,0.45)',
                color: '#84cc16',
              }}
            >
              Copy link
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
